import { defineComponent, ref } from "vue";
import { Key } from "../../../../../types/skyward/svg";
import Button from "../../../../core/button";
import { VueBrowseClasses } from "../../classes";
import { browseStore } from "../../store";
import { Classes, getButton, isSmallScreen } from "./common";
import listBodyDesktop from "./desktop/listBody";
import listBodyMobile from "./mobile/listBody";
import { ApplicationStatuses, JobPosting, JobPostingStore } from "./types";

export default defineComponent(
    {
        name: "listBody",
        setup(_: unknown, context: { slots: any })
        {
            const state = browseStore.injectBrowseState<JobPostingStore, JobPosting>();
            if (!state)
            {
                window.skyward.error.reportException(new ReferenceError("Unable to retrieve browse state"));
                return () => null;
            }
            const dataSource = state.features.dataSource;

            return isSmallScreen()
                ? listBodyMobile(dataSource, state)
                : listBodyDesktop(dataSource, state, context.slots);
        },
    }
);

/**
 * Generates a Span with a `label` and `value` if the value is truthy and `additionalCondition` is true
 */
export function conditionalField(label: string | null, value: any, additionalCondition: boolean = true, className: "viewAllInfoLine" | "mobileInfoLine" = "viewAllInfoLine"): JSX.Element | null
{
    if (value && additionalCondition)
    {
        return (
            <div class={className}>
                <span>
                    <label class="bold">{label}</label>
                    <label>{value}</label>
                </span>
            </div>
        );
    }
    return null;
}

/** Creates a row stating there are no records */
export function noRows(colSpan: number = 1): JSX.Element
{
    return (
        <tr class={VueBrowseClasses.BodyRow}>
            <td class={VueBrowseClasses.BodyCell} colspan={colSpan}>
                <div class={[VueBrowseClasses.BodyCellContent, VueBrowseClasses.BodyCellContentEmpty]}>
                    No records to display
                </div>
            </td>
        </tr>
    );
}

export function getFavoriteButtons(applicantID: number,
    jobPostingID: number,
    isFavorite: boolean,
    disabled: boolean): JSX.Element[]
{
    let favoriteButtonRef = ref<typeof Button>();
    let unfavoriteButtonRef = ref<typeof Button>();
    const favoriteButtonCallback = function (isFavoriting: boolean, applicantID: number, jobPostingID: number)
    {
        let action = isFavoriting ? 'FavoriteJobPostingViewJobPostings' : 'UnfavoriteJobPostingViewJobPostings';

        if (applicantID !== null)
        {
            favoriteButtonRef?.value?.classList.toggle(Classes.DisplayNone);
            unfavoriteButtonRef?.value?.classList.toggle(Classes.DisplayNone);

            $.ajax(window.skyward.global.virtualDir + `ApplicantTracking/JobBoard/${action}/${jobPostingID}`);
        }
        else
        {
            window.skyward.window.loadContent('full', 'ApplicantTracking', 'JobBoard', action, jobPostingID);
        }
    }
    return [
        getButton("",
            "StarFavoritesMenu",
            () => favoriteButtonCallback(false, applicantID, jobPostingID),
            {
                reference: favoriteButtonRef,
                visible: isFavorite,
                disabled: disabled,
                buttonClass: "favorite"
            }),
        getButton("",
            "StarFavoritesOutline",
            () => favoriteButtonCallback(true, applicantID, jobPostingID),
            {
                reference: unfavoriteButtonRef,
                visible: !isFavorite,
                disabled: disabled,
                buttonClass: "favorite"
            }),
    ];
}

export function getApplyButton(jobPostingID: number, applicationStatus: ApplicationStatuses, disabled: boolean): JSX.Element
{
    let applyButtonText: string;
    let applyIcon: Key;
    switch (applicationStatus)
    {
        case ApplicationStatuses.Started:
            applyButtonText = "In Progress";
            applyIcon = "InProcess";
            break;
        case ApplicationStatuses.Submitted:
            applyButtonText = "Submitted";
            applyIcon = "Check";
            break;
        default: //NotApplied
            applyButtonText = "Apply";
            applyIcon = "InlineEdit";
            break;
    }
    const applyButtonCallback = function(jobPostingID: number)
    {
        window.skyward.window.loadContent('full', 'ApplicantTracking', 'Application', 'Apply', jobPostingID);
    }
    return getButton(applyButtonText, applyIcon, () => applyButtonCallback(jobPostingID), {disabled: disabled, spanClass: "underline", buttonClass: "apply"});
}

export function redirectToViewAll(jobPostingID: number): void
{
    window.skyward.window.loadContent('popout', 'ApplicantTracking', 'JobBoard', 'ViewAll', jobPostingID);
}
